import moment from 'moment-timezone';

const initializeInterviewPlan = ({
  interviewConfigs,
  suggestions,
  _candidate = {},
}) => {
  const interviewPlans = {};

  // the corresponding interview plans for each interview suggestion
  interviewConfigs.forEach((plan) => {
    const { interview, ...rest } = plan;

    const currentSuggestion = suggestions.find(
      (i) => i.job_stage_action_id === interview.job_stage_action_id
    );

    const interviewersDetails = [];

    interview?.interviewers.forEach((int) => {
      const isPresentInSuggestion =
        currentSuggestion?.recommended_interviewers.includes(Number(int?.id));
      if (isPresentInSuggestion) {
        interviewersDetails.push({
          ...int,
        });
      }
    });

    // the unique id here can be the job_stage_action_id
    // convert the data to the format of the interview plan required in the scheduler details
    interviewPlans[interview.job_stage_action_id] = {
      ...interview,
      ...rest,
      interviewersDetails,
      end_time: currentSuggestion?.end_time,
      start_time: currentSuggestion?.start_time,
      interviewer_status: currentSuggestion?.interviewer_status,
      isOriginalSlotModified: false,
      calendar_info: null, // Initialize calendar_info to track user selections
    };
  });

  return interviewPlans;
};

const mergeSchedulerDataIntoPlan = ({
  originalSuggestion,
  currentPlan,
  newData,
}) => {
  const finalData = {
    ...currentPlan,
    // updated data overrides the existing data
    name: newData?.interviewName,
    substituted_name: newData?.interviewName,
    location: newData?.interviewLocation,
    kind: newData?.interviewKind,
    start_time: newData?.bookedSlot?.start,
    end_time: newData?.bookedSlot?.end,
    duration: newData?.bookedSlot?.duration,
    interviewersDetails: newData?.interviewersDetails,
    host: { id: newData?.hostId },
    meeting_rooms: newData?.meetingRooms,
    meeting_rooms_enabled: newData?.meetingRooms?.length > 0,
    meetingRoomObj: newData?.meetingRoomObj,
    isOriginalSlotModified:
      originalSuggestion?.start_time !== newData?.bookedSlot?.start ||
      originalSuggestion?.end_time !== newData?.bookedSlot?.end,
    timezone:
      newData?.datesSet?.timeZone ??
      newData?.bookedSlot?.timezone ??
      currentPlan?.timezone,
    // Persist selected calendar info between steps in bulk manual mode
    calendar_info: newData?.calendar_info ?? currentPlan?.calendar_info,

    // Preserve AI Notetaker and Interview Private options
    note_taker_enabled:
      newData?.note_taker_enabled ?? currentPlan?.note_taker_enabled,
    note_taker_mandatory:
      newData?.note_taker_mandatory ?? currentPlan?.note_taker_mandatory,
    calendar_visibility:
      newData?.calendar_visibility ?? currentPlan?.calendar_visibility,

    // Preserve office address data
    ats_office: newData?.ats_office ?? currentPlan?.ats_office,
    ats_office_id: newData?.ats_office_id ?? currentPlan?.ats_office_id,
  };

  const result = {
    ...finalData,
    candidate_comm_template: {
      ...(currentPlan?.candidate_comm_template || {}),
      body:
        newData?.candidate_comm_template?.body ??
        currentPlan?.candidate_comm_template?.body,
    },
    reviewer_comm_template: {
      ...(currentPlan?.reviewer_comm_template || {}),
      body:
        newData?.reviewer_comm_template?.body ??
        currentPlan?.reviewer_comm_template?.body,
    },
  };

  return result;
};

const isConflictingWithBulkBookedEvents = (
  bulkBookedEvents = [],
  bookedEvent
) => {
  const { start, end, timezone } = bookedEvent;

  const filteredBulkBookedEvents = bulkBookedEvents?.filter(
    (event) => !event.isActive
  );

  const startMoment = moment.tz(start, timezone);
  const endMoment = moment.tz(end, timezone);

  return filteredBulkBookedEvents?.some((bulkBookedEvent) => {
    const bulkBookedEventStart = moment.tz(bulkBookedEvent.start, timezone);
    const bulkBookedEventEnd = moment.tz(bulkBookedEvent.end, timezone);
    return (
      startMoment.isBefore(bulkBookedEventEnd) &&
      endMoment.isAfter(bulkBookedEventStart)
    );
  });
};

const generatePayload = ({ interviewPlans, applicationData, timezone }) => {
  const interviews = [];

  const {
    id: atsApplicationId,
    contact,
    job_id,
    job_stage_id: ats_job_stage_id,
  } = applicationData ?? {};

  // Loop through each job stage action in the data
  Object.keys(interviewPlans).forEach((actionId) => {
    const currentInterviewData = interviewPlans[actionId];

    // Create interview object based on the expected payload structure
    const interview = {
      // Basic interview information
      name: currentInterviewData.name,
      location: currentInterviewData.location,
      kind: currentInterviewData.kind,

      ats_job_stage_action_id: parseInt(actionId),
      ats_job_id: job_id,
      ats_job_stage_id,
      ats_application_id: atsApplicationId,

      // Participant IDs - extract from interviewers array
      participant_ids: currentInterviewData.interviewersDetails
        ? currentInterviewData.interviewersDetails.map(
            (interviewer) => interviewer.id
          )
        : [],

      // Time-related fields
      timezone: currentInterviewData.timezone ?? timezone,
      duration: currentInterviewData.duration,
      start_time: currentInterviewData.start_time,
      end_time: currentInterviewData.end_time,

      // Templates and descriptions
      interviewer_template_id: currentInterviewData.reviewer_comm_template?.id,
      interviewer_description:
        currentInterviewData.reviewer_comm_template?.body,
      candidate_template_id: currentInterviewData.candidate_comm_template?.id,
      candidate_description: currentInterviewData.candidate_comm_template?.body,

      // Note taker settings
      note_taker_enabled: currentInterviewData.note_taker_enabled,
      note_taker_mandatory: currentInterviewData.note_taker_mandatory,

      // Calendar visibility (Interview Private option)
      calendar_visibility: currentInterviewData.calendar_visibility,
      // Calendar info for shared calendar selection
      calendar_info: currentInterviewData.calendar_info,
      // Schedule modification flags
      schedule_type_modified: currentInterviewData.isOriginalSlotModified,
      schedule_slots_modified: currentInterviewData.isOriginalSlotModified,

      candidate_id: contact?.id,

      // Scorecard configuration
      scorecard_feedback_description:
        currentInterviewData.scorecard?.feedback_description,
      scorecard_rating_description:
        currentInterviewData.scorecard?.rating_description,
      scorecard_config: {
        questions:
          currentInterviewData.scorecard?.questions?.map((q) => {
            return {
              type: q.type,
              question: q.question,
              description: q.description,
            };
          }) || [],
      },
    };

    // Add host_id only for zoom interviews
    if (
      currentInterviewData.location === 'zoom' &&
      currentInterviewData.host?.id
    ) {
      interview['host_id'] = currentInterviewData.host.id;
    }

    // Add office address for onsite interviews
    if (currentInterviewData.location === 'onsite') {
      const officeId =
        currentInterviewData.ats_office_id ||
        currentInterviewData.ats_office?.id;

      if (officeId) {
        interview['ats_office_id'] = officeId;
      }

      if (currentInterviewData.ats_office) {
        interview['ats_office'] = currentInterviewData.ats_office;
      }
    }

    // Add meeting rooms if enabled
    if (
      currentInterviewData.meeting_rooms_enabled &&
      currentInterviewData.meetingRoomObj
    ) {
      interview['meeting_rooms_attributes'] = Object.keys(
        currentInterviewData.meetingRoomObj
      ).map((email) => ({
        name: currentInterviewData.meetingRoomObj[email],
        email: email,
      }));
    }

    interviews.push(interview);
  });

  return { interviews };
};

export const bulkManualSchedulerUtils = {
  initializeInterviewPlan,
  mergeSchedulerDataIntoPlan,
  isConflictingWithBulkBookedEvents,
  generatePayload,
};
